/*
 * base-entity.js — the base Entity descriptor (recovered `S`/`w` from
 * PlaceEntity.js): the itemCfg SHAPE, cross-kind capability flags, the
 * descriptor defaults `{ id, allowed, settings:{layout:{sections:[]}} }`, the
 * route builders, and typeDecorators. Every built-in kind extends this.
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
 * (flag names manageable/removable/hasPagination/hasFilter/hasSearch/localSearch/
 * hasEntityCard/hasMap/localSort/batchRemove/hasCustomFields/selectMode/
 * defaultSortProperty/defaultSortDirection("ACS")/rootPath("/entity/view")/
 * builderRoot("/entity/builder"); routes().fetch="entity/read"; the getViewRoute/
 * getBuilderRoute/getCreatePath/getEditPath string shapes; typeDecorators keys
 * employee/multi_employee/file/image), internal impl re-authored.
 */

import { Model } from './model.js';

export class BaseEntity extends Model {
  defaults() {
    return {
      // ---- capability flags (overridable per kind) ----
      manageable: true, removable: true,
      hasPagination: true, noTotalCount: false,
      hasFilter: true, hasSearch: true, localSearch: false,
      hasEntityCard: true, hasMap: true, localSort: false,
      batchRemove: false, fileNamesInThumb: true,
      hasCustomFields: false, hasFullscreen: true, hasAfterCreateConfirm: false,
      selectMode: 'checkbox',
      defaultSortProperty: 'label', defaultSortDirection: 'ACS' /* sic */,
      rootPath: '/entity/view', builderRoot: '/entity/builder',
      letterField: null, formComponents: null, slots: null,
      serverType: null, fileStorageType: null, fields: null,
      permissions: null, tariffFeature: null, storeList: null,
      type: null, addAnotherItemMsg: null,
      // ---- descriptor payload ----
      id: null, allowed: true, settings: { layout: { sections: [] } },
    };
  }

  /* Default DESCRIPTOR fetch route; kinds override itemCfg for record CRUD. */
  routes() { return { fetch: 'entity/read' }; }

  getEffectiveType() { return this.serverType || this.type; }
  getFileStorageType() { return this.fileStorageType || this.getEffectiveType(); }

  /* Descriptor fetch params: custom entity keyed by id, built-ins by type. */
  paramsDecorators() {
    const self = this;
    return {
      fetch() {
        const eff = self.getEffectiveType();
        return eff === 'custom' ? { id: self.id } : { type: eff };
      },
    };
  }

  /* Merge server layout with the default layout; map each field's sortable
   * from the registry. Kinds that synthesize sections client-side override. */
  responseDecorators() {
    return { fetch: (payload) => payload };
  }

  /* Decode reference-type values on the way in. */
  typeDecorators() {
    return {
      employee: (value) => value,
      multi_employee: (value) => value,
      file: (value) => value,
      image: (value) => value,
    };
  }

  /* ---- route builders (verbatim string shapes) ---- */
  getViewRoute() { return `${this.rootPath}/${this.type}/${this.id || '-'}`; }
  getBuilderRoute() { return `${this.builderRoot}/${this.getEffectiveType()}/${this.id}`; }
  getCreatePath() {
    const e = this.id || '-';
    return this.formComponents ? `${this.rootPath}/${this.type}/${e}/custom/create`
      : `${this.rootPath}/${this.type}/${e}/create`;
  }
  getEditPath() {
    const e = this.id || '-';
    return this.formComponents ? `${this.rootPath}/${this.type}/${e}/custom/edit/`
      : `${this.rootPath}/${this.type}/${e}/edit/`;
  }

  isManageable() {
    return this.manageable;
  }

  /* Descriptor sections helper for the form view. */
  getSections() {
    return (this.settings && this.settings.layout && this.settings.layout.sections) || [];
  }

  /* Async descriptor loader (kinds synthesize sections here). Base is a no-op. */
  async fetch() { return this; }
}

export default BaseEntity;
